import React, { useState, useEffect, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, Zap, X, FileText, 
  DollarSign, ShieldCheck, Activity, Shield 
} from 'lucide-react';
import { secureFetch, ROUTES } from "@utils/apiClient";
import { useAvaPolling } from '../../hooks/useAvaPolling';
import VaultExplorer from '../../components/VaultExplorer';

/**
 * PRINCIPAL COMMAND CENTER (ADMIN)
 * ROLE: Merged Forensic Oversight & Live Intent Telemetry
 * BRANDING: Micheals Infrastructure Advisory (Principal Strategy)
 * VERSION: 5.1.4 (Defensive Logic + Dispatch Safety)
 */

const PrincipalCommandCenter = ({ backendUrl }) => {
    const [leads, setLeads] = useState([]);
    const [selectedLead, setSelectedLead] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [vaultFiles, setVaultFiles] = useState([]);
    const [consultantNotes, setConsultantNotes] = useState("");
    const [performance, setPerformance] = useState({ monthlyRevenue: 0, consultationCount: 0 });
    const [liveLeads, setLiveLeads] = useState([]);
    const [liveAlert, setLiveAlert] = useState(null);

    const refreshData = useCallback(async () => {
        try {
            const resLeads = await secureFetch(ROUTES.VERIFIED_SESSIONS);
            const dataLeads = await resLeads.json();
            
            // Logic-First Guard: Ensure we always have an array
            setLeads(Array.isArray(dataLeads) ? dataLeads : []);

            const resPerf = await secureFetch(ROUTES.PERFORMANCE_REPORT);
            const dataPerf = await resPerf.json();
            if (dataPerf.success) setPerformance(dataPerf.report);
        } catch (err) {
            console.error("Archive Sync Error:", err);
            setLeads([]);
        }
    }, []);

    const { socket, isConnected } = useAvaPolling(backendUrl, "ADMIN_OVERWATCH");

    useEffect(() => {
        refreshData();
        if (!socket) return;

        socket.on('PRINCIPAL_ALERT', (data) => {
            setLiveLeads(prev => [data, ...prev].slice(0, 8));
            setLiveAlert({ ...data, type: 'INTENT' });
            setTimeout(() => setLiveAlert(null), 5000);
            refreshData(); 
        });

        socket.on('REVENUE_CONFIRMED', (data) => {
            setLiveAlert({ ...data, type: 'REVENUE' });
            setPerformance(prev => ({
                ...prev,
                monthlyRevenue: prev.monthlyRevenue + (data.amount || 0),
                consultationCount: (prev.consultationCount || 0) + 1
            }));
            setTimeout(() => setLiveAlert(null), 8000);
            refreshData(); 
        });

        return () => {
            socket.off('PRINCIPAL_ALERT');
            socket.off('REVENUE_CONFIRMED');
        };
    }, [socket, refreshData]);

    const handleGenerateBrief = async () => {
        if (!selectedLead) return false;
        setIsGenerating(true);
        
        try {
            const response = await secureFetch(ROUTES.GENERATE_BRIEF, {
                method: 'POST',
                body: JSON.stringify({ 
                    lead: selectedLead, 
                    files: vaultFiles,
                    notes: consultantNotes 
                })
            });

            if (!response.ok) throw new Error("Synthesis Protocol Failed");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Strategic_Brief_${selectedLead.id?.substring(0,8) || 'ARCHIVE'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            setConsultantNotes("");
            refreshData();
            return true;
            
        } catch (err) {
            console.error("Briefing Dispatch Error:", err);
            return false;
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#fafaf9] p-8 md:p-12 font-sans selection:bg-amber-100">
            <AnimatePresence>
                {liveAlert && (
                    <motion.div 
                        initial={{ opacity: 0, y: 50, x: '-50%' }} 
                        animate={{ opacity: 1, y: 0, x: '-50%' }} 
                        exit={{ opacity: 0 }}
                        className={`fixed bottom-10 left-1/2 z-[200] px-8 py-4 rounded-full shadow-2xl flex items-center gap-4 border ${
                            liveAlert.type === 'REVENUE' 
                            ? 'bg-amber-600 text-white border-amber-400' 
                            : 'bg-stone-900 text-white border-amber-500/30'
                        }`}
                    >
                        {liveAlert.type === 'REVENUE' ? <DollarSign size={18} /> : <Zap className="text-amber-500 animate-pulse" size={18} />}
                        <span className="text-sm font-serif italic">
                            {liveAlert.type === 'REVENUE' 
                                ? `Confirmed: $${liveAlert.amount} from ${liveAlert.customer || 'Client'}`
                                : `High Intent: ${liveAlert.sessionId?.substring(0,8)} on ${liveAlert.path || 'System Hub'}`
                            }
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>

            <header className="max-w-7xl mx-auto mb-12 flex justify-between items-end border-b border-stone-200 pb-8">
                <div>
                    <h1 className="text-[10px] uppercase tracking-[0.5em] text-stone-400 font-bold mb-2">Micheals Infrastructure Advisory</h1>
                    <p className="text-4xl font-serif text-stone-900 leading-tight">Principal Oversight</p>
                    <div className="flex items-center gap-2 mt-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                        <span className="text-[9px] uppercase tracking-widest text-stone-500 font-mono">
                            {isConnected ? 'Uplink Secure' : 'Uplink Disconnected'}
                        </span>
                    </div>
                </div>
                <div className="flex gap-12">
                    <StatBox label="Intake (30d)" value={`$${performance?.monthlyRevenue.toLocaleString() || '0'}`} color="text-amber-600" />
                    <StatBox label="Active Briefs" value={leads.length} color="text-stone-900" />
                </div>
            </header>

            <main className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden h-fit">
                    <div className="px-8 py-5 border-b border-stone-100 flex justify-between items-center bg-stone-50/50">
                        <div className="flex items-center gap-3">
                            <Activity size={14} className="text-amber-600" />
                            <h2 className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Asset Registry</h2>
                        </div>
                    </div>
                    <table className="w-full text-left">
                        <thead className="bg-stone-50 text-[9px] uppercase tracking-widest text-stone-400 border-b border-stone-100">
                            <tr>
                                <th className="px-8 py-4 font-bold">Client / Reference</th>
                                <th className="px-8 py-4 font-bold">Status</th>
                                <th className="px-8 py-4 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {leads.length > 0 ? leads.map((lead) => (
                                <tr key={lead.id} className="hover:bg-stone-50/50 transition-all cursor-pointer group" onClick={() => { setSelectedLead(lead); setIsModalOpen(true); }}>
                                    <td className="px-8 py-6">
                                        <p className="font-serif text-lg text-stone-900">{lead.customerName || 'Pending Identification'}</p>
                                        <p className="text-[10px] font-mono text-stone-400 uppercase tracking-tighter">{lead.id?.substring(0,12)}</p>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-1.5 h-1.5 rounded-full ${lead.status === 'COMPLETED' ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`} />
                                            <span className="text-[10px] font-bold text-stone-800 uppercase tracking-widest">
                                                {lead.status === 'COMPLETED' ? 'Dispatched' : 'Pending Audit'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        <button className="p-3 border border-stone-200 group-hover:bg-stone-900 group-hover:text-white transition-all">
                                            <BarChart3 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="3" className="px-8 py-10 text-center text-stone-400 italic text-sm font-serif">
                                        No active sessions in current registry.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="space-y-4">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-stone-400 flex items-center gap-2 mb-4">
                        <Zap size={12} /> Live Engagement Log
                    </h3>
                    {liveLeads.map((item, i) => (
                        <div key={i} className="bg-stone-900 border-l-4 border-l-amber-600 p-5 shadow-sm flex justify-between items-center animate-in fade-in slide-in-from-right-4">
                            <div>
                                <p className="font-mono text-[9px] text-stone-500 uppercase">Node: {item.sessionId?.slice(-8)}</p>
                                <p className="text-xs font-bold text-stone-100 truncate max-w-[150px]">{item.path || 'SYSTEM_HUB'}</p>
                            </div>
                            <div className="text-right">
                                <span className="text-lg font-serif text-amber-500">{item.score || '0'}%</span>
                                <p className="text-[8px] uppercase tracking-widest text-stone-500">Intent</p>
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            <AnimatePresence>
                {isModalOpen && selectedLead && (
                    <ForensicSlideout 
                        lead={selectedLead} 
                        onClose={() => setIsModalOpen(false)} 
                        vaultFiles={vaultFiles}
                        setVaultFiles={setVaultFiles}
                        notes={consultantNotes}
                        setNotes={setConsultantNotes}
                        onDispatch={handleGenerateBrief}
                        isGenerating={isGenerating}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

const StatBox = memo(({ label, value, color }) => (
    <div className="text-right">
        <p className="text-[9px] uppercase font-bold tracking-widest text-stone-400 mb-1">{label}</p>
        <p className={`text-3xl font-serif ${color}`}>{value}</p>
    </div>
));

const ForensicSlideout = ({ lead, onClose, vaultFiles, setVaultFiles, notes, setNotes, onDispatch, isGenerating }) => {
    const [dispatchStatus, setDispatchStatus] = useState('IDLE'); 

    const handleInternalDispatch = async () => {
        setDispatchStatus('GENERATING');
        const success = await onDispatch(); 
        
        if (success) {
            setDispatchStatus('SUCCESS');
            setTimeout(() => {
                setDispatchStatus('IDLE');
                onClose();
            }, 3000);
        } else {
            setDispatchStatus('IDLE');
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-end">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} className="relative w-full max-w-3xl bg-stone-950 h-full shadow-2xl p-12 overflow-y-auto">
                <button onClick={onClose} className="text-stone-500 hover:text-white mb-12 flex items-center gap-2 text-[10px] uppercase font-bold tracking-[0.3em]"><X size={14}/> Close Archive</button>
                
                <div className="flex items-start justify-between mb-2">
                    <h2 className="text-5xl font-serif text-white leading-tight">{lead.customerName || 'Unknown Entity'}</h2>
                    <ShieldCheck className={dispatchStatus === 'SUCCESS' ? "text-emerald-500 transition-colors duration-500" : "text-amber-500"} size={32} />
                </div>
                <p className="text-amber-500 font-mono text-sm mb-12 italic">{lead.customerEmail}</p>
                
                <div className="mb-12">
                    <h3 className="text-[10px] uppercase font-bold text-stone-500 mb-6 tracking-widest flex items-center gap-2">
                        <FileText size={12} /> Forensic Asset Vault
                    </h3>
                    <VaultExplorer sessionId={lead.stripeSessionId || lead.id} onFilesLoaded={setVaultFiles} />
                </div>

                <div className="mb-8 border-t border-stone-800 pt-12">
                    <label className="text-[10px] uppercase font-bold text-stone-500 mb-3 tracking-[0.2em] flex items-center gap-2">
                        <Shield size={12} className="text-amber-500" /> Principal’s Forensic Observations
                    </label>
                    <textarea 
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full h-48 bg-stone-900/30 border border-stone-800 rounded-sm p-5 text-sm text-stone-300 font-serif focus:border-amber-600 outline-none transition-all placeholder:text-stone-700 resize-none leading-relaxed"
                        placeholder="Synthesize infrastructure risks..."
                    />
                </div>

                <motion.button 
                    onClick={handleInternalDispatch} 
                    disabled={dispatchStatus !== 'IDLE' || isGenerating} 
                    animate={dispatchStatus === 'SUCCESS' ? { scale: [1, 1.02, 1] } : {}}
                    className={`w-full py-5 font-bold uppercase text-[10px] tracking-widest transition-all duration-500 ${
                        dispatchStatus === 'SUCCESS' 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-amber-600 text-stone-950 hover:bg-white'
                    } disabled:opacity-50`}
                >
                    <div className="flex items-center justify-center gap-3">
                        {(dispatchStatus === 'GENERATING' || isGenerating) && <Zap size={14} className="animate-pulse" />}
                        {dispatchStatus === 'SUCCESS' && <ShieldCheck size={14} />}
                        
                        <span>
                            {dispatchStatus === 'IDLE' && "Dispatch Strategic Brief"}
                            {(dispatchStatus === 'GENERATING' || isGenerating) && "Synthesizing Protocol..."}
                            {dispatchStatus === 'SUCCESS' && "Brief Verified & Downloaded"}
                        </span>
                    </div>
                </motion.button>
            </motion.div>
        </div>
    );
};

export default PrincipalCommandCenter;